import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { getLocalizedCategoryName } from '@/lib/localization-helpers'
import { Main } from '@/components/layout/main'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { VariableAutocomplete } from '@/components/variable-autocomplete'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2 } from 'lucide-react'
import {
  FileText,
  ArrowLeft,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileUp,
  Plus,
  Settings,
  ChevronDown,
  ChevronRight,
  Code,
  Copy,
  Check,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useToast } from '@/hooks/use-toast'
import {
  create_cabinet_template,
  validate_word_template_cabinet,
  list_template_categories,
  list_cabinet_template_variables,
  create_cabinet_template_variable,
} from '@/api/document-templates-cabinet'
import type { TemplateVariable, TemplateVariableCreate } from '@/types/document-template'
import Quill from 'quill'
import 'quill/dist/quill.snow.css'

export const Route = createFileRoute('/_authenticated/cabinet-document-template-create')({
  component: Page,
})

function Page() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({ title: '', slug: '' })

  const [engine, setEngine] = useState<'html' | 'word'>('html')
  const [htmlBody, setHtmlBody] = useState<string>('')
  const [categories, setCategories] = useState<Array<{ id: string; name: string; name_ar?: string }>>([])
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('')
  const quillRef = useRef<Quill | null>(null)
  const editorContainerRef = useRef<HTMLDivElement | null>(null)

  // Word import state
  const [wordFile, setWordFile] = useState<File | null>(null)
  const [validating, setValidating] = useState(false)
  const [validationResult, setValidationResult] = useState<{
    valid: boolean
    variables: string[]
    valid_variables?: string[]
    invalid_variables?: string[]
    message?: string
    errors?: string[]
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Variable autocomplete state
  const [autocompletePosition, setAutocompletePosition] = useState<{ top: number; left: number } | null>(null)
  const [autocompleteSearch, setAutocompleteSearch] = useState('')
  const autocompleteIndexRef = useRef<number | null>(null)

  // Variables management state
  const [showVariablesSection, setShowVariablesSection] = useState(false)
  const [availableVariables, setAvailableVariables] = useState<TemplateVariable[]>([])
  const [loadingVariables, setLoadingVariables] = useState(false)
  const [showCreateVariableDialog, setShowCreateVariableDialog] = useState(false)
  const [newVariableData, setNewVariableData] = useState<{
    key: string
    label: string
    label_ar: string
    variable_type: 'string' | 'number' | 'date' | 'boolean'
    description: string
    example: string
  }>({ key: '', label: '', label_ar: '', variable_type: 'string', description: '', example: '' })
  const [creatingVariable, setCreatingVariable] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const copyToClipboard = (key: string) => {
    navigator.clipboard.writeText(`{{${key}}}`)
    setCopiedKey(key)
    setTimeout(() => setCopiedKey(null), 2000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.docx')) {
      toast({ title: t('error') || 'Error', description: t('invalid_file_type') || 'Please select a .docx file', variant: 'destructive' })
      return
    }

    setWordFile(file)
    setValidationResult(null)
    setValidating(true)
    try {
      const result = await validate_word_template_cabinet(file)
      setValidationResult(result)
      if (result.valid) {
        toast({ title: t('validation_success') || 'Validation Successful', description: `${t('found_variables') || 'Found'} ${result.variables.length} ${t('variables') || 'variables'}` })
      } else {
        toast({ title: t('validation_failed') || 'Validation Failed', description: `${result.invalid_variables?.length || 0} ${t('invalid_variables') || 'invalid variables'} found`, variant: 'destructive' })
      }
    } catch (error: any) {
      const errorData = error?.response?.data
      if (errorData && typeof errorData === 'object' && 'valid' in errorData) {
        setValidationResult(errorData)
        toast({ title: t('validation_failed') || 'Validation Failed', description: errorData.errors?.[0] || t('invalid_variables_found') || 'Invalid variables found', variant: 'destructive' })
      } else {
        toast({ title: t('validation_error') || 'Validation Error', description: error?.message || t('failed_to_validate') || 'Failed to validate document', variant: 'destructive' })
      }
    } finally {
      setValidating(false)
    }
  }

  const handleVariableSelect = (variableKey: string) => {
    const quill = quillRef.current
    if (quill && autocompleteIndexRef.current !== null) {
      const currentSelection = quill.getSelection()
      const currentIndex = currentSelection?.index || autocompleteIndexRef.current
      const deleteLength = currentIndex - autocompleteIndexRef.current
      if (deleteLength > 0) {
        quill.deleteText(autocompleteIndexRef.current, deleteLength)
      }
      const variableText = `{{${variableKey}}}`
      quill.insertText(autocompleteIndexRef.current, variableText)
      const textAfter = quill.getText(autocompleteIndexRef.current + variableText.length, 1)
      if (textAfter && textAfter !== ' ' && textAfter !== '\n') {
        quill.insertText(autocompleteIndexRef.current + variableText.length, ' ')
      }
      const finalCursorPos = autocompleteIndexRef.current + variableText.length + (textAfter && textAfter !== ' ' && textAfter !== '\n' ? 1 : 0)
      quill.setSelection(finalCursorPos)
      quill.focus()
    }
    setAutocompletePosition(null)
    autocompleteIndexRef.current = null
    setAutocompleteSearch('')
  }

  useEffect(() => {
    if (editorContainerRef.current && !quillRef.current) {
      quillRef.current = new Quill(editorContainerRef.current, {
        theme: 'snow',
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean'],
          ],
        },
      })

      editorContainerRef.current.setAttribute('dir', 'rtl')

      quillRef.current.on('text-change', (_delta, _oldDelta, source) => {
        const html = quillRef.current?.root.innerHTML || ''
        setHtmlBody(html)

        if (source === 'user') {
          const selection = quillRef.current?.getSelection()
          if (selection) {
            const text = quillRef.current?.getText(0, selection.index) || ''
            const lastTwoChars = text.slice(-2)

            if (lastTwoChars === '{{' && !autocompletePosition) {
              const bounds = quillRef.current?.getBounds(selection.index)
              if (bounds && editorContainerRef.current) {
                const editorRect = editorContainerRef.current.getBoundingClientRect()
                const position = {
                  top: editorRect.top + bounds.top + bounds.height,
                  left: editorRect.left + bounds.left,
                }
                setAutocompletePosition(position)
                setAutocompleteSearch('')
                autocompleteIndexRef.current = selection.index - 2
              }
            } else if (autocompleteIndexRef.current !== null) {
              const currentText = quillRef.current?.getText() || ''
              const beforeCursor = currentText.substring(0, selection.index)
              const lastOpenBraceIndex = beforeCursor.lastIndexOf('{{')

              if (lastOpenBraceIndex >= autocompleteIndexRef.current - 1) {
                const searchStart = lastOpenBraceIndex + 2
                const searchEnd = selection.index
                const searchText = currentText.slice(searchStart, searchEnd)

                if (searchText.includes('}}')) {
                  setAutocompletePosition(null)
                  autocompleteIndexRef.current = null
                  setAutocompleteSearch('')
                } else {
                  setAutocompleteSearch(searchText)
                  if (!autocompletePosition) {
                    const bounds = quillRef.current?.getBounds(selection.index)
                    if (bounds && editorContainerRef.current) {
                      const editorRect = editorContainerRef.current.getBoundingClientRect()
                      setAutocompletePosition({ top: editorRect.top + bounds.top + bounds.height, left: editorRect.left + bounds.left })
                    }
                  }
                }
              } else {
                setAutocompletePosition(null)
                autocompleteIndexRef.current = null
                setAutocompleteSearch('')
              }
            }
          }
        }
      })
    }
  }, [])

  // Fetch categories
  useEffect(() => {
    const load = async () => {
      try {
        const cats = await list_template_categories()
        setCategories(cats)
      } catch (_) {}
    }
    load()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.slug.trim()) {
      toast({ title: t('error') || 'Error', description: t('fill_required_fields') || 'Please fill in all required fields', variant: 'destructive' })
      return
    }

    if (engine === 'html' && !htmlBody.trim()) {
      toast({ title: t('error') || 'Error', description: t('template_content_required') || 'Please provide template content', variant: 'destructive' })
      return
    }

    if (engine === 'word' && !wordFile) {
      toast({ title: t('error') || 'Error', description: t('word_file_required') || 'Please upload a Word document', variant: 'destructive' })
      return
    }

    if (engine === 'word' && validationResult && !validationResult.valid) {
      toast({ title: t('error') || 'Error', description: t('fix_validation_errors') || 'Please fix validation errors before submitting', variant: 'destructive' })
      return
    }

    setIsLoading(true)
    try {
      await create_cabinet_template({
        slug: formData.slug,
        title: formData.title,
        engine: engine,
        html_body: engine === 'html' ? htmlBody : undefined,
        category: selectedCategoryId || null,
        file: engine === 'word' ? wordFile! : undefined,
      })

      toast({ title: t('success') || 'Success', description: t('template_created') || 'Document template created successfully' })
      navigate({ to: '/cabinet-document-templates' })
    } catch (error: any) {
      const errorMsg = error?.detail || error?.message || t('error_creating_template') || 'Failed to create template'
      toast({ title: t('error') || 'Error', description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg), variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadVariables = async () => {
    setLoadingVariables(true)
    try {
      const response = await list_cabinet_template_variables({ page_size: 50 })
      setAvailableVariables(response.results)
    } catch (_) {}
    finally {
      setLoadingVariables(false)
    }
  }

  const handleCreateVariable = async () => {
    if (!newVariableData.key || !newVariableData.label) {
      toast({ title: t('error') || 'Error', description: t('fill_required_fields') || 'Please fill required fields', variant: 'destructive' })
      return
    }

    if (!/^[a-z_][a-z0-9_]*(\.[a-z_][a-z0-9_]*)*$/i.test(newVariableData.key)) {
      toast({ title: t('error') || 'Error', description: t('invalid_key_format') || 'Key must be in format: entity.property (e.g., client.name)', variant: 'destructive' })
      return
    }

    setCreatingVariable(true)
    try {
      const payload: TemplateVariableCreate = {
        key: newVariableData.key,
        label: newVariableData.label,
        label_ar: newVariableData.label_ar || undefined,
        variable_type: newVariableData.variable_type,
        description: newVariableData.description || undefined,
        example: newVariableData.example || undefined,
      }
      await create_cabinet_template_variable(payload)
      setShowCreateVariableDialog(false)
      setNewVariableData({ key: '', label: '', label_ar: '', variable_type: 'string', description: '', example: '' })
      loadVariables()
      toast({ title: t('success') || 'Success', description: t('variable_created_successfully') || 'Variable created successfully' })
    } catch (error: any) {
      const errorMsg = error?.detail || error?.key?.[0] || error?.message
      toast({ title: t('error_creating_variable') || 'Failed to create variable', description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg), variant: 'destructive' })
    } finally {
      setCreatingVariable(false)
    }
  }

  const insertVariableIntoEditor = (variableKey: string) => {
    const quill = quillRef.current
    if (quill) {
      const selection = quill.getSelection()
      const index = selection ? selection.index : quill.getLength()
      const variableText = `{{${variableKey}}}`
      quill.insertText(index, variableText)
      const textAfter = quill.getText(index + variableText.length, 1)
      if (textAfter && textAfter !== ' ' && textAfter !== '\n') {
        quill.insertText(index + variableText.length, ' ')
      }
      const finalPos = index + variableText.length + (textAfter && textAfter !== ' ' && textAfter !== '\n' ? 1 : 0)
      quill.setSelection(finalPos)
      quill.focus()
    }
  }

  useEffect(() => {
    if (showVariablesSection && availableVariables.length === 0) {
      loadVariables()
    }
  }, [showVariablesSection])

  return (
    <>
      <Main>
        <div className='mb-6'>
          <Button
            variant='ghost'
            size='sm'
            className='mb-4 gap-2'
            onClick={() => navigate({ to: '/cabinet-document-templates' })}
            disabled={isLoading}
          >
            <ArrowLeft className='w-4 h-4' />
            {t('back') || 'Back'}
          </Button>

          <div className='flex items-center gap-3 mb-4'>
            <FileText className='w-8 h-8' />
            <div>
              <h2 className='text-2xl font-bold tracking-tight'>
                {t('create_template') || 'Create Document Template'}
              </h2>
              <p className='text-muted-foreground'>
                {t('create_new_template_desc') || 'Create a new document template for your cabinet'}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='max-w-6xl space-y-6'>
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>{t('template_details') || 'Template Details'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label htmlFor='title'>
                    {t('title') || 'Title'} <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='title'
                    name='title'
                    value={formData.title}
                    onChange={handleChange}
                    placeholder={t('enter_template_title') || 'نموذج عقد خدمة'}
                    disabled={isLoading}
                  />
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='slug'>
                    {t('slug') || 'Slug'} <span className='text-destructive'>*</span>
                  </Label>
                  <Input
                    id='slug'
                    name='slug'
                    value={formData.slug}
                    onChange={handleChange}
                    placeholder='contract-template'
                    disabled={isLoading}
                  />
                  <p className='text-xs text-muted-foreground'>
                    {t('slug_help') || 'Unique identifier (lowercase, hyphens only)'}
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='engine'>
                    {t('template_engine') || 'Template Engine'} <span className='text-destructive'>*</span>
                  </Label>
                  <Select value={engine} onValueChange={(val) => setEngine(val as 'html' | 'word')}>
                    <SelectTrigger id='engine'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='html'>
                        {t('html_editor') || 'HTML Editor'}
                      </SelectItem>
                      <SelectItem value='word'>
                        {t('word_document') || 'Word Document (.docx)'}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className='text-xs text-muted-foreground'>
                    {engine === 'html'
                      ? (t('html_editor_desc') || 'Create template using visual editor')
                      : (t('word_document_desc') || 'Upload a Word document with {{variables}}')}
                  </p>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='category'>
                    {t('category') || 'Category'}
                  </Label>
                  <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
                    <SelectTrigger id='category'>
                      <SelectValue placeholder={t('select_category') || 'Select a category'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {getLocalizedCategoryName(c, i18n.language)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Word Document Import */}
          {engine === 'word' && (
            <Card>
              <CardHeader>
                <CardTitle className='flex items-center gap-2'>
                  <FileUp className='w-5 h-5' />
                  {t('upload_word_document') || 'Upload Word Document'}
                </CardTitle>
                <CardDescription>
                  {t('upload_word_desc') || 'Upload a .docx file with variables in the format {{variable.name}}'}
                </CardDescription>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-3'>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.docx'
                    onChange={handleFileSelect}
                    className='hidden'
                  />

                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={validating || isLoading}
                    className='w-full gap-2'
                  >
                    <Upload className='w-4 h-4' />
                    {wordFile ? (t('change_file') || 'Change File') : (t('select_file') || 'Select File')}
                  </Button>

                  {wordFile && (
                    <div className='p-3 rounded-md bg-muted flex items-center gap-3'>
                      <FileText className='w-5 h-5 text-muted-foreground' />
                      <div className='flex-1 min-w-0'>
                        <p className='text-sm font-medium truncate'>{wordFile.name}</p>
                        <p className='text-xs text-muted-foreground'>{(wordFile.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                  )}

                  {validating && (
                    <Alert>
                      <AlertDescription className='flex items-center gap-2'>
                        <div className='w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin' />
                        {t('validating_document') || 'Validating document...'}
                      </AlertDescription>
                    </Alert>
                  )}

                  {validationResult && (
                    <Alert variant={validationResult.valid ? 'default' : 'destructive'}>
                      <AlertDescription>
                        <div className='flex items-start gap-2'>
                          {validationResult.valid ? (
                            <CheckCircle2 className='w-5 h-5 mt-0.5 flex-shrink-0 text-green-600' />
                          ) : (
                            <AlertCircle className='w-5 h-5 mt-0.5 flex-shrink-0' />
                          )}
                          <div className='flex-1 space-y-3'>
                            <p className='font-medium'>
                              {validationResult.valid ? (t('validation_passed') || 'Validation Passed') : (t('validation_failed') || 'Validation Failed')}
                            </p>

                            {validationResult.errors && validationResult.errors.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-destructive'>{t('errors') || 'Errors'}:</p>
                                <div className='space-y-1'>
                                  {validationResult.errors.map((error, i) => (
                                    <div key={i} className='text-sm text-destructive/90 bg-destructive/5 p-2 rounded border border-destructive/20'>{error}</div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {validationResult.valid_variables && validationResult.valid_variables.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-green-700'>{t('valid_variables') || 'Valid Variables'} ({validationResult.valid_variables.length}):</p>
                                <div className='flex flex-wrap gap-1'>
                                  {validationResult.valid_variables.map((v, i) => (
                                    <Badge key={i} variant='secondary' className='text-xs bg-green-100 text-green-800'>✓ {v}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}

                            {validationResult.invalid_variables && validationResult.invalid_variables.length > 0 && (
                              <div className='space-y-1'>
                                <p className='text-sm font-medium text-destructive'>{t('invalid_variables') || 'Invalid Variables'} ({validationResult.invalid_variables.length}):</p>
                                <div className='flex flex-wrap gap-1'>
                                  {validationResult.invalid_variables.map((v, i) => (
                                    <Badge key={i} variant='destructive' className='text-xs'>✗ {v}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* WYSIWYG Editor - Only for HTML engine */}
          {engine === 'html' && (
            <Card>
              <CardHeader>
                <CardTitle className='text-base'>{t('template_content') || 'Template Content'}</CardTitle>
                <CardDescription>
                  {t('template_content_desc') || 'Use the rich text editor to compose your document. Insert variables to personalize content.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='mb-3 p-3 rounded-md bg-muted/50 text-sm'>
                  <p className='font-medium mb-1'>{t('tip') || 'Tip'}:</p>
                  <p className='text-muted-foreground'>
                    {t('variable_autocomplete_help') || 'Type {{ in the editor to open variable autocomplete.'}
                  </p>
                </div>
                <div
                  ref={editorContainerRef}
                  className='min-h-[300px] rounded-md border border-input bg-background'
                />
              </CardContent>
            </Card>
          )}

          {/* Variables Management Section */}
          <Card>
            <CardHeader>
              <Collapsible open={showVariablesSection} onOpenChange={setShowVariablesSection}>
                <CollapsibleTrigger asChild>
                  <div className='flex items-center justify-between w-full cursor-pointer select-none'>
                    <div className='flex items-center gap-2'>
                      <Code className='w-5 h-5' />
                      <CardTitle className='text-base'>{t('template_variables') || 'Template Variables'}</CardTitle>
                      {availableVariables.length > 0 && (
                        <Badge variant='secondary' className='text-xs'>{availableVariables.length}</Badge>
                      )}
                    </div>
                    {showVariablesSection ? (
                      <ChevronDown className='w-4 h-4 text-muted-foreground' />
                    ) : (
                      <ChevronRight className='w-4 h-4 text-muted-foreground' />
                    )}
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent className='space-y-4 pt-3'>
                  <CardDescription>
                    {t('variables_management_desc') || 'Browse system variables and manage your custom variables. Click the copy button to copy the syntax.'}
                  </CardDescription>

                  <div className='flex justify-between items-center'>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => setShowCreateVariableDialog(true)}
                      className='gap-2'
                    >
                      <Plus className='w-4 h-4' />
                      {t('create_variable') || 'Create Variable'}
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={loadVariables}
                      disabled={loadingVariables}
                      className='gap-2'
                    >
                      <Settings className='w-4 h-4' />
                      {loadingVariables ? (t('loading') || 'Loading...') : (t('refresh') || 'Refresh')}
                    </Button>
                  </div>

                  {loadingVariables && (
                    <div className='flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground'>
                      <Loader2 className='w-4 h-4 animate-spin' />
                      {t('loading_variables') || 'Loading variables...'}
                    </div>
                  )}

                  {!loadingVariables && availableVariables.length === 0 && (
                    <div className='p-6 text-center text-muted-foreground rounded-md border border-dashed'>
                      <Code className='w-8 h-8 mx-auto mb-2 opacity-30' />
                      <p className='text-sm'>{t('no_variables_found') || 'No variables found'}</p>
                      <p className='text-xs mt-1'>{t('create_first_variable') || 'Create your first variable to get started'}</p>
                    </div>
                  )}

                  {!loadingVariables && availableVariables.length > 0 && (() => {
                    const systemVars = availableVariables.filter(v => v.source === 'system')
                    const customVars = availableVariables.filter(v => v.source !== 'system')

                    const renderVariable = (variable: TemplateVariable) => (
                      <div
                        key={variable.id}
                        className='group flex items-start gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition-colors'
                      >
                        {/* Syntax chip + copy */}
                        <div className='flex flex-col items-start gap-1 min-w-0'>
                          <div className='flex items-center gap-1.5 rounded-md border border-border bg-muted px-2.5 py-1'>
                            <code className='text-xs font-mono font-semibold text-foreground'>{'{{' + variable.key + '}}'}</code>
                            <button
                              type='button'
                              title={t('copy') || 'Copy'}
                              onClick={() => copyToClipboard(variable.key)}
                              className='ml-1 text-muted-foreground hover:text-foreground transition-colors'
                            >
                              {copiedKey === variable.key ? (
                                <Check className='w-3.5 h-3.5 text-green-600' />
                              ) : (
                                <Copy className='w-3.5 h-3.5' />
                              )}
                            </button>
                          </div>
                          <div className='flex items-center gap-1 flex-wrap'>
                            <Badge
                              variant={variable.source === 'system' ? 'default' : 'secondary'}
                              className='text-[10px] px-1.5 py-0'
                            >
                              {variable.source === 'system' ? (t('system') || 'System') : (t('custom') || 'Custom')}
                            </Badge>
                            {variable.variable_type && (
                              <Badge variant='outline' className='text-[10px] px-1.5 py-0 text-muted-foreground'>
                                {variable.variable_type}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Label + description */}
                        <div className='flex-1 min-w-0 pt-0.5'>
                          <p className='text-sm font-medium leading-tight'>{variable.label_ar || variable.label}</p>
                          {variable.label !== variable.label_ar && variable.label && variable.label_ar && (
                            <p className='text-xs text-muted-foreground'>{variable.label}</p>
                          )}
                          {variable.description && (
                            <p className='text-xs text-muted-foreground mt-0.5 line-clamp-2'>
                              {variable.description_ar || variable.description}
                            </p>
                          )}
                          {variable.example && (
                            <p className='text-xs text-muted-foreground/70 mt-0.5 italic'>
                              ex: {variable.example}
                            </p>
                          )}
                        </div>

                        {/* Insert button (HTML only) */}
                        {engine === 'html' && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => insertVariableIntoEditor(variable.key)}
                            className='shrink-0 gap-1 h-8 text-xs opacity-0 group-hover:opacity-100 transition-opacity'
                          >
                            <Plus className='w-3 h-3' />
                            {t('insert') || 'Insert'}
                          </Button>
                        )}
                      </div>
                    )

                    return (
                      <div className='space-y-4 max-h-96 overflow-y-auto pr-1'>
                        {systemVars.length > 0 && (
                          <div className='space-y-2'>
                            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1'>
                              {t('system_variables') || 'System Variables'} ({systemVars.length})
                            </p>
                            <div className='space-y-1.5'>
                              {systemVars.map(renderVariable)}
                            </div>
                          </div>
                        )}
                        {customVars.length > 0 && (
                          <div className='space-y-2'>
                            <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1'>
                              {t('custom_variables') || 'Custom Variables'} ({customVars.length})
                            </p>
                            <div className='space-y-1.5'>
                              {customVars.map(renderVariable)}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </CollapsibleContent>
              </Collapsible>
            </CardHeader>
          </Card>

          {/* Submit Button */}
          <div className='flex justify-end gap-3'>
            <Button
              type='button'
              variant='outline'
              onClick={() => navigate({ to: '/cabinet-document-templates' })}
              disabled={isLoading}
            >
              {t('cancel') || 'Cancel'}
            </Button>
            <Button type='submit' disabled={isLoading || validating}>
              {isLoading ? (t('creating') || 'Creating...') : (t('create_template') || 'Create Template')}
            </Button>
          </div>
        </form>

        {/* Variable Autocomplete */}
        {engine === 'html' && (
          <VariableAutocomplete
            position={autocompletePosition}
            searchText={autocompleteSearch}
            onSelect={handleVariableSelect}
            onClose={() => {
              setAutocompletePosition(null)
              autocompleteIndexRef.current = null
              setAutocompleteSearch('')
            }}
          />
        )}

        {/* Create Variable Dialog */}
        <Dialog open={showCreateVariableDialog} onOpenChange={setShowCreateVariableDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('create_new_variable') || 'Create Custom Variable'}</DialogTitle>
            </DialogHeader>

            <div className='space-y-4 py-4'>
              <div className='space-y-2'>
                <Label htmlFor='new-key'>
                  {t('variable_key') || 'Key'} <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='new-key'
                  value={newVariableData.key}
                  onChange={(e) => setNewVariableData(prev => ({ ...prev, key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder='duree_contrat'
                  disabled={creatingVariable}
                  autoFocus
                />
                <p className='text-xs text-muted-foreground'>
                  {t('key_format_help') || 'Lowercase letters, numbers, underscores (e.g., duree_contrat)'}
                </p>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-label'>
                  {t('variable_label') || 'Label'} <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='new-label'
                  value={newVariableData.label}
                  onChange={(e) => setNewVariableData(prev => ({ ...prev, label: e.target.value }))}
                  placeholder='Contract Duration'
                  disabled={creatingVariable}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-label-ar'>{t('variable_label_ar') || 'Label (Arabic)'}</Label>
                <Input
                  id='new-label-ar'
                  value={newVariableData.label_ar}
                  onChange={(e) => setNewVariableData(prev => ({ ...prev, label_ar: e.target.value }))}
                  placeholder='مدة العقد'
                  disabled={creatingVariable}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-type'>{t('variable_type') || 'Type'}</Label>
                <Select
                  value={newVariableData.variable_type}
                  onValueChange={(v) => setNewVariableData(prev => ({ ...prev, variable_type: v as any }))}
                >
                  <SelectTrigger id='new-type'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='string'>{t('string') || 'String (text)'}</SelectItem>
                    <SelectItem value='number'>{t('number') || 'Number'}</SelectItem>
                    <SelectItem value='date'>{t('date') || 'Date'}</SelectItem>
                    <SelectItem value='boolean'>{t('boolean') || 'Boolean (yes/no)'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-description'>{t('description') || 'Description'}</Label>
                <Input
                  id='new-description'
                  value={newVariableData.description}
                  onChange={(e) => setNewVariableData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder='Duration in months'
                  disabled={creatingVariable}
                />
              </div>

              <div className='space-y-2'>
                <Label htmlFor='new-example'>{t('example') || 'Example'}</Label>
                <Input
                  id='new-example'
                  value={newVariableData.example}
                  onChange={(e) => setNewVariableData(prev => ({ ...prev, example: e.target.value }))}
                  placeholder='12 months'
                  disabled={creatingVariable}
                />
              </div>

              {/* Preview */}
              <div className='p-3 rounded-md bg-muted/50 space-y-2'>
                <p className='text-xs font-medium text-muted-foreground'>{t('preview') || 'Preview'}:</p>
                <code className='text-sm font-mono bg-background px-2 py-1 rounded block'>
                  {`{{${newVariableData.key || '...'}}}`}
                </code>
                {newVariableData.label && (
                  <p className='text-sm text-foreground'>{newVariableData.label}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant='outline' onClick={() => setShowCreateVariableDialog(false)} disabled={creatingVariable}>
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleCreateVariable}
                disabled={creatingVariable || !newVariableData.key || !newVariableData.label}
              >
                {creatingVariable && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
                {creatingVariable ? (t('creating_variable') || 'Creating...') : (t('create') || 'Create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Main>
    </>
  )
}
